import React from "react";
import {useSearchParams} from "react-router";
import type {GridSortModel} from "@mui/x-data-grid-premium";
import type {ContractLifecycle, LinkStatus} from "./contractExplorerTypes.ts";
import {LIFECYCLES, LINK_STATUSES} from "./contractExplorerTypes.ts";

/**
 * Everything the page shows, kept in the URL: the list's search, filters,
 * sort and page, and the open bundle with its tab. Opening a bundle and
 * going back restores the list, and either view can be shared as a link.
 */
export type ContractListParams = {
    search: string;
    lifecycle: ContractLifecycle | null;
    /** A document type from the summary's `type_counts`. */
    type: string | null;
    needsAttention: boolean;
    /** The API's `ordering`: a key from `ORDERING_KEYS`, `-` prefix = descending. */
    ordering: string;
    /** Zero-based page of the list, as the grid counts them. */
    page: number;
    pageSize: number;
    /** The open bundle; null shows the list. */
    contract: number | null;
    tab: ContractTab;
    /** The Spend tab's link status. */
    links: LinkStatus;
};

export type ContractTab = 'details' | 'spend' | 'opportunities';

const TABS: ContractTab[] = ['details', 'spend', 'opportunities'];

export const DEFAULT_ORDERING = '-linked_spend';
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** The sort keys the API accepts (DRF `OrderingFilter`), without the `-` prefix. */
export const ORDERING_KEYS = [
    'title', 'supplier_name', 'document_type', 'starts_on', 'ends_on', 'linked_spend', 'weighted_saving',
    'document_count', 'id',
] as const;

type OrderingKey = typeof ORDERING_KEYS[number];

function isValidOrdering(value: string | null | undefined): value is string {
    if (!value) return false;
    return (ORDERING_KEYS as readonly string[]).includes(value.replace(/^-/, ''));
}

/** Grid column field → API ordering key. Columns not listed here are not sortable. */
const SORT_FIELD_TO_ORDERING: Record<string, OrderingKey> = {
    title: 'title',
    supplier_name: 'supplier_name',
    document_type: 'document_type',
    ends_on: 'ends_on',
    linked_spend: 'linked_spend',
    weighted_saving: 'weighted_saving',
    document_count: 'document_count',
};

/** Grid sort model → the API's `ordering`. An empty model (sort cleared) means the default. */
export function toOrdering(sortModel: GridSortModel): string {
    const [sort] = sortModel;
    const key = sort ? SORT_FIELD_TO_ORDERING[sort.field] : undefined;
    return key ? `${sort.sort === 'desc' ? '-' : ''}${key}` : DEFAULT_ORDERING;
}

/** The API's `ordering` → grid sort model, for the sort indicator. */
export function toSortModel(ordering: string): GridSortModel {
    const value = isValidOrdering(ordering) ? ordering : DEFAULT_ORDERING;
    const key = value.replace(/^-/, '');
    const field = Object.keys(SORT_FIELD_TO_ORDERING).find(f => SORT_FIELD_TO_ORDERING[f] === key);
    return field ? [{field, sort: value.startsWith('-') ? 'desc' : 'asc'}] : [];
}

export function readContractListParams(params: URLSearchParams): ContractListParams {
    const lifecycle = params.get('lifecycle');
    const ordering = params.get('ordering');
    const links = params.get('links');
    return {
        search: params.get('search') ?? '',
        lifecycle: LIFECYCLES.includes(lifecycle as ContractLifecycle) ? lifecycle as ContractLifecycle : null,
        type: params.get('type'),
        needsAttention: params.get('attention') === '1',
        ordering: isValidOrdering(ordering) ? ordering : DEFAULT_ORDERING,
        // 1-based in the URL (what a user reads), 0-based everywhere else.
        page: Math.max((Number(params.get('page')) || 1) - 1, 0),
        pageSize: PAGE_SIZE_OPTIONS.includes(Number(params.get('page_size')))
            ? Number(params.get('page_size'))
            : DEFAULT_PAGE_SIZE,
        contract: Number(params.get('contract')) || null,
        tab: TABS.includes(params.get('tab') as ContractTab) ? params.get('tab') as ContractTab : 'details',
        links: LINK_STATUSES.includes(links as LinkStatus) ? links as LinkStatus : 'linked',
    };
}

export function useContractListParams() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = React.useMemo(() => readContractListParams(searchParams), [searchParams]);

    /** Patch the params. Opening or closing a bundle pushes a history entry, so Back
     *  returns to the list; everything else replaces, so Back does not walk each keystroke. */
    const update = React.useCallback((patch: Partial<ContractListParams>) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            const merged = {...readContractListParams(previous), ...patch};
            // Narrowing the list invalidates the page number (an explicit page in the patch wins).
            const narrowing: (keyof ContractListParams)[] = ['search', 'lifecycle', 'type', 'needsAttention', 'ordering'];
            if (patch.page === undefined && narrowing.some(key => key in patch)) merged.page = 0;
            // Another bundle starts on its Details tab and its linked transactions.
            if ('contract' in patch && patch.contract !== readContractListParams(previous).contract) {
                merged.tab = patch.tab ?? 'details';
                merged.links = patch.links ?? 'linked';
            }
            const write = (key: string, value: string | null, omitWhen?: string) => {
                if (!value || value === omitWhen) next.delete(key);
                else next.set(key, value);
            };
            write('search', merged.search);
            write('lifecycle', merged.lifecycle);
            write('type', merged.type);
            write('attention', merged.needsAttention ? '1' : null);
            write('ordering', merged.ordering, DEFAULT_ORDERING);
            write('page', merged.page > 0 ? String(merged.page + 1) : null);
            write('page_size', String(merged.pageSize), String(DEFAULT_PAGE_SIZE));
            write('contract', merged.contract ? String(merged.contract) : null);
            write('tab', merged.contract ? merged.tab : null, 'details');
            write('links', merged.contract ? merged.links : null, 'linked');
            return next;
        }, {replace: !('contract' in patch)});
    }, [setSearchParams]);

    return {params, update};
}
